import { useCallback, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { SortingState } from "@tanstack/react-table";

import { TableColumnSettingsProvider } from "@providers/TableColumnSettingsProvider/TableColumnSettingsProvider";

import {
  AdminApplicationFilterForm,
  AdminApplicationFilterFormRef,
  AdminApplicationFilterFormValues,
} from "@components/Forms/AdminApplicationFilterForm/AdminApplicationFilterForm";
import { ApplicationsTable } from "@components/Tables/ApplicationsTable/ApplicationsTable";
import {
  areAdminFiltersActive,
  getDefaultAdminFilterValues,
} from "@components/Tables/ApplicationsTable/ApplicationsTableConfig";
import { ApplicationsTableColumnCustomization } from "@components/Tables/ApplicationsTable/UI/ApplicationsTableColumnCustomization";
import {
  ADMIN_APPLICATION_TABLE_COLUMN_ORDER,
  ADMIN_APPLICATION_TABLE_COLUMN_SETTINGS,
} from "@components/Tables/ApplicationsTable/UI/ApplicationsTableColumns";
import { useApplicationTableExport } from "@components/Tables/ApplicationsTable/utils/useApplicationTableExport";

import { Box } from "@design-system/Box/Box";
import { Card } from "@design-system/Card/Card";
import { IconButton } from "@design-system/IconButton/IconButton";
import { SlideInPanel } from "@design-system/SlideInPanel";
import { Text } from "@design-system/Text/Text";

export const AdminApplicationsCard = () => {
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [isColumnSettingsOpen, setIsColumnSettingsOpen] = useState(false);
  const [filterValues, setFilterValues] = useState<AdminApplicationFilterFormValues>({
    ...getDefaultAdminFilterValues(),
    categoryId: undefined,
  });
  const [sorting, setSorting] = useState<SortingState>([]);

  const filterFormRef = useRef<AdminApplicationFilterFormRef>(null);

  const filtersActive = useMemo(() => {
    return areAdminFiltersActive(filterValues);
  }, [filterValues]);

  const handleFilterClick = useCallback(() => {
    setIsFilterPanelOpen(true);
  }, []);

  const handleCloseFilterPanel = useCallback(() => {
    setIsFilterPanelOpen(false);
  }, []);

  const handleFiltersReset = useCallback(() => {
    setFilterValues(getDefaultAdminFilterValues());
  }, []);

  const handleFilterChange = useCallback(
    (newValues: AdminApplicationFilterFormValues) => {
      setFilterValues(newValues);
      setIsFilterPanelOpen(false);
    },
    [],
  );

  const handleApplyFilters = useCallback(() => {
    if (filterFormRef.current) {
      const currentFormValues = filterFormRef.current.getCurrentValues();
      setFilterValues(currentFormValues);
    }
  }, []);

  const handleColumnSettingsClick = useCallback(() => {
    setIsColumnSettingsOpen(true);
  }, []);

  const handleCloseColumnSettings = useCallback(() => {
    setIsColumnSettingsOpen(false);
  }, []);

  const { exportData, isExporting } = useApplicationTableExport({
    tableId: "admin-applications",
    exportType: "csv",
    vendorNameSearch: filterValues.vendorNameSearch,
    productNameSearch: filterValues.productNameSearch,
    productDomainNameSearch: filterValues.productDomainNameSearch,
    isAdminView: true,
    isSaasFilter: filterValues.isSaasFilter,
    sorting,
  });

  const handleExportToCsv = useCallback(async () => {
    try {
      await exportData();
      toast.success("Applications exported successfully!");
    } catch (error) {
      toast.error("Failed to export applications");
      console.error("Export error:", error);
    }
  }, [exportData]);

  return (
    <TableColumnSettingsProvider
      tableId="admin-applications"
      defaultColumnSettings={ADMIN_APPLICATION_TABLE_COLUMN_SETTINGS}
      defaultColumnOrder={ADMIN_APPLICATION_TABLE_COLUMN_ORDER}
    >
      <Card grow>
        <Card.Header withBottomPadding>
          <Card.Header.Title>
            Applications (Administrative View)
          </Card.Header.Title>
          <Card.Header.Actions>
            <Box direction="row" gap={8} align="center">
              {filtersActive && (
                <Text size={12} color={"textSecondary"}>
                  Filters are applied
                </Text>
              )}
              <IconButton
                size="sm"
                icon="faFilter"
                onClick={handleFilterClick}
                outline={"sm"}
                showBubble={filtersActive}
                tooltip="Show filters"
              />
              <IconButton
                size="sm"
                icon="faTableColumns"
                onClick={handleColumnSettingsClick}
                outline={"sm"}
                tooltip="Show/Hide columns"
              />
              <IconButton
                size="sm"
                icon={isExporting ? "faArrowsSpin" : "faFileCsv"}
                onClick={handleExportToCsv}
                outline={"sm"}
                tooltip={
                  isExporting ? "Exporting..." : "Export table data to CSV"
                }
                disabled={isExporting}
              />
            </Box>
          </Card.Header.Actions>
        </Card.Header>
        <Card.Body noPadding grow>
          <ApplicationsTable
            isAdminView={true}
            {...(filterValues.vendorNameSearch && {
              vendorNameSearch: filterValues.vendorNameSearch,
            })}
            {...(filterValues.productNameSearch && {
              productNameSearch: filterValues.productNameSearch,
            })}
            {...(filterValues.productDomainNameSearch && {
              productDomainNameSearch: filterValues.productDomainNameSearch,
            })}
            isSaasFilter={filterValues.isSaasFilter}
            {...(filterValues.categoryId && {
              categoryId: filterValues.categoryId,
            })}
            sorting={sorting}
            onSortingChange={setSorting}
          />
        </Card.Body>
      </Card>

      <SlideInPanel
        isOpen={isFilterPanelOpen}
        onClose={handleCloseFilterPanel}
        onCancel={handleFiltersReset}
        onApply={handleApplyFilters}
        cancelButtonText={filtersActive ? "Clear filters" : undefined}
        applyButtonText="Apply filters"
        title="Application Filters"
      >
        <AdminApplicationFilterForm
          ref={filterFormRef}
          onFilterChange={handleFilterChange}
          values={filterValues}
          errorsAPI={undefined}
        />
      </SlideInPanel>

      <ApplicationsTableColumnCustomization
        isOpen={isColumnSettingsOpen}
        onClose={handleCloseColumnSettings}
      />
    </TableColumnSettingsProvider>
  );
};
